import orderBy from 'lodash/orderBy'
import partition from 'lodash/partition'
import React, { useCallback, useState } from 'react'

import {
  Box,
  Divider,
  EmptyState,
  FormOnChange,
  Searchbar,
  Stack,
  Text,
} from '@procsea/design-system'

import { useCurrentMembership, useUser } from 'src/contexts/userContext'
import { getMembershipName } from 'src/utils'

import MembershipContextMenuItem from '../MembershipContextMenuItem/MembershipContextMenuItem'
import { ListSubtitle, MembershipList } from './MembershipContextMenuContent.styles'

interface MembershipFilters {
  search?: string
}

const MembershipContextMenuContent = () => {
  const [filters, setFilters] = useState<MembershipFilters>({})
  const currentMembership = useCurrentMembership()
  const currentMembershipName = getMembershipName(currentMembership)
  const user = useUser()

  const filteredMemberships =
    user?.memberships.filter(
      membership =>
        membership.id !== currentMembership.id &&
        (!filters?.search ||
          getMembershipName(membership).toLowerCase().includes(filters.search.toLowerCase()))
    ) ?? []

  const [sellerMemberships, buyerMemberships] = partition(filteredMemberships, 'isSeller')

  const orderedSellerMemberships = orderBy(
    sellerMemberships,
    [membership => getMembershipName(membership).toLocaleLowerCase()],
    ['asc']
  )

  const orderedBuyerMemberships = orderBy(
    buyerMemberships,
    [membership => getMembershipName(membership).toLocaleLowerCase()],
    ['asc']
  )

  const handleSearchChange: FormOnChange<string> = useCallback(({ value }) => {
    setFilters({ search: value })
  }, [])

  return (
    <Box inset="small">
      <Stack spacing="medium">
        <Text data-e2e="membership-context-menu-title" size="small" heading variant="secondary">
          {currentMembershipName}
        </Text>

        <Divider />

        <Searchbar
          data-e2e="membership-context-menu-search"
          name="search"
          fluid
          onChange={handleSearchChange}
          placeholder={gettext('Search...')}
          value={filters?.search}
        />

        <MembershipList spacing="xxsmall">
          {!!orderedSellerMemberships.length && (
            <>
              <ListSubtitle data-e2e="sale-subtitle">{gettext('Sale')}</ListSubtitle>
              {orderedSellerMemberships.map(membership => (
                <MembershipContextMenuItem key={membership.id} membership={membership} />
              ))}
            </>
          )}

          {!!orderedBuyerMemberships.length && (
            <>
              <ListSubtitle data-e2e="purchase-subtitle">{gettext('Purchase')}</ListSubtitle>
              {orderedBuyerMemberships.map(membership => (
                <MembershipContextMenuItem key={membership.id} membership={membership} />
              ))}
            </>
          )}

          {!orderedSellerMemberships.length && !orderedBuyerMemberships.length && (
            <EmptyState
              data-e2e="membership-context-menu-empty-state"
              icon="search"
              primaryText={gettext('No companies found')}
              secondaryText={gettext('Try with other search criteria')}
            />
          )}
        </MembershipList>
      </Stack>
    </Box>
  )
}

export default MembershipContextMenuContent
